import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {
  Box,
  Divider,
  InputBase,
  MenuItem,
  Modal,
  Select,
  Slider,
  Typography,
  Stack,
  Avatar,
  Chip,
} from '@mui/material';
import { filter, isEmpty } from 'lodash';
import React from 'react';
import CAN from '../../../images/can.svg';
import Button from '../../../components/Button';
import Switch from '../../../components/Switch';
import WarningIcon from '@mui/icons-material/Warning';
import IconButton from '../../../components/IconButton';
import { ICON_BUTTON_TYPE } from '../../../constants';
import {
  updateDeviceCaregiversApi,
  updateDeviceConfigApi,
  updateDeviceInfoApi,
  updateRecipientDeviceConfigApi,
  updateRecipientDeviceInfoApi,
  updateUserEmergencyApi,
} from '../../../utils/api';
import { useMutation } from 'react-query';
import useStore from '../../../Store/Store';
import { refresh } from '../../../utils';

const DeviceConfigurationsModal = ({ show, onClose, device, user: recipient, cr = false }) => {
  const [TABS, setTABS] = React.useState(['General', 'Additional features', 'Caregivers']);
  const [tab, setTab] = React.useState(TABS[0]);
  const user = useStore((state) => state.user);
  const caregivers = useStore((state) => state.caregivers);

  React.useEffect(() => {
    if (cr) {
      setTABS((prev) => prev.slice(0, 2));
    }
  }, [cr]);

  const [state, setState] = React.useState({
    deviceName: '',
    volume: 0,
    sleep: 0,
    fallDetection: false,
    whitelist: false,
    firmware: false,
    canCompanion: false,
    errorMessage: '',
  });

  const [caregiversData, setCaregiversData] = React.useState({});

  React.useEffect(() => {
    if (!isEmpty(caregivers)) {
      setCaregiversData(caregivers);
    }
  }, [caregivers]);

  React.useEffect(() => {
    if (!isEmpty(device)) {
      setState((prev) => {
        return {
          ...prev,
          deviceName: device?.name || '',
          volume: device?.config?.volume || 0,
          sleep: device?.config?.sleep || '',
          fallDetection: device?.config?.dropAlert,
          whitelist: device?.config?.whitelistEnable,
          firmware: device?.config?.autoPilot?.state,
          canCompanion: device?.config?.CANassistant,
        };
      });
    }
  }, [device]);

  const updateDeviceInfoHandler = useMutation(updateDeviceInfoApi, {
    onSuccess: async ({ data }) => {
      onClose();
    },
    onError: (error) => {
      console.log('error =====> ', error);
      if (error?.response?.status === 400) {
        return setState((prev) => {
          return { ...prev, errorMessage: error?.response?.data?.data?.join(',') || 'Something went wrong!' };
        });
      } else {
        return setState((prev) => {
          return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
        });
      }
    },
  });

  const updateRecipientDeviceInfoHandler = useMutation(updateRecipientDeviceInfoApi, {
    onSuccess: async ({ data }) => {
      onClose();
    },
    onError: (error) => {
      console.log('error =====> ', error);
      if (error?.response?.status === 400) {
        return setState((prev) => {
          return { ...prev, errorMessage: error?.response?.data?.data?.join(',') || 'Something went wrong!' };
        });
      } else {
        return setState((prev) => {
          return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
        });
      }
    },
  });

  const updateDeviceConfigHandler = useMutation(updateDeviceConfigApi, {
    onSuccess: async ({ data }) => {
      onClose();
    },
    onError: (error) => {
      console.log('error =====> ', error);
      return setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
      });
    },
  });

  const updateRecipientDeviceConfigHandler = useMutation(updateRecipientDeviceConfigApi, {
    onSuccess: async ({ data }) => {
      onClose();
    },
    onError: (error) => {
      console.log('error =====> ', error);
      return setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
      });
    },
  });

  const updateDeviceCaregivers = useMutation(updateDeviceCaregiversApi, {
    onSuccess: async ({ data }) => {
      onClose();
    },
    onError: (error) => {
      console.log('error =====> ', error);
      return setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
      });
    },
  });

  const updateUserEmergency = useMutation(updateUserEmergencyApi, {
    onSuccess: async ({ data }) => {
      await refresh();
    },
    onError: (error) => {
      console.log('error =====> ', error);
      return setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const { deviceName, volume, sleep, fallDetection, whitelist, firmware, canCompanion } = state;

    setState((prev) => {
      return { ...prev, errorMessage: '' };
    });

    if (tab === TABS[0]) {
      const body = {
        deviceName,
        volume: 0,
        sleep: 4,
      };
      if (cr) {
        updateRecipientDeviceInfoHandler.mutate({
          userId: user?._id,
          recipientId: recipient?._id,
          deviceId: device?._id,
          body,
        });
      } else {
        updateDeviceInfoHandler.mutate({ userId: user?._id, deviceId: device?._id, body });
      }
    } else if (tab === TABS[1]) {
      const body = {
        fallDetection,
        whitelist,
        firmware,
        canCompanion,
      };
      if (cr) {
        updateRecipientDeviceConfigHandler.mutate({
          userId: user?._id,
          recipientId: recipient?._id,
          deviceId: device?._id,
          body,
        });
      } else {
        updateDeviceConfigHandler.mutate({ userId: user?._id, deviceId: device?._id, body });
      }
    } else {
      updateDeviceCaregivers.mutate({ userId: user?._id, body: caregiversData });
    }
  };

  const handleSwitchChange = (itemId, deviceId) => {
    setCaregiversData((prev) => {
      const currentDevices = prev[itemId]?.devices || [];
      const isDeviceLinked = currentDevices.find((id) => id.deviceId === deviceId);

      const updatedDevices = !isEmpty(isDeviceLinked)
        ? filter(currentDevices, (id) => id.deviceId !== deviceId)
        : [...currentDevices, { deviceId }];

      return {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          devices: updatedDevices,
        },
      };
    });
  };

  const handleSetEmergency = (userId, caregiverId) => {
    updateUserEmergency.mutate({ userId, caregiverId });
  };

  return (
    <Modal open={show} onClose={onClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '50%',
          p: 6,
          bgcolor: 'background.paper',
          borderRadius: 2.5,
          position: 'relative',
          top: 30,
          left: '23%',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={5}>
          <Typography variant="h5">CAN Go Settings</Typography>
          <IconButton onClick={onClose} type={ICON_BUTTON_TYPE.close} />
        </Box>

        <Box display="flex" mb={5}>
          {TABS.map((item, idx) => (
            <Box
              key={idx}
              variant="contained"
              sx={{
                borderRadius: 50,
                mr: 2,
                cursor: 'pointer',
                color: 'grey',
                background: tab === item ? '#f2f2f2' : '#fff',
                fontWeight: tab === item ? '600' : 'normal',
                padding: '13px',
              }}
              onClick={() => setTab(item)}
            >
              {item}
            </Box>
          ))}
        </Box>

        {tab === TABS[0] && (
          <Box display="flex" mb={1}>
            <Box display="flex" flexDirection="column" alignItems="center" mr={6}>
              <img src={CAN} alt="Device" style={{ width: 200, height: 220, borderRadius: 8 }} />
              <Box display="flex" mt={2}>
                <Button text="Change a photo" bg="light" />
                <IconButton onClick={onClose} type={ICON_BUTTON_TYPE.delete} />
              </Box>
            </Box>

            <Box flex={1}>
              <Box mb={2}>
                <Typography variant="body1" gutterBottom>
                  Device name
                </Typography>
                <InputBase
                  fullWidth
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 50,
                    px: 2,
                    py: 1,
                  }}
                  onChange={(e) =>
                    setState((prev) => {
                      return { ...prev, deviceName: e.target.value };
                    })
                  }
                  value={state.deviceName}
                />
                <Typography variant="body2" color="textSecondary" mt={1}>
                  Use different names for your canes to make it easy to differentiate them.
                </Typography>
              </Box>
              {/* 
              <Box mb={2}>
                <Typography variant="body1" gutterBottom>
                  Volume Control
                </Typography>
                <Box display="flex" alignItems="center">
                  <VolumeUpIcon />
                  <Slider
                    aria-labelledby="volume-slider"
                    valueLabelDisplay="auto"
                    sx={{ mx: 2, flex: 1, color: '#26786A' }}
                    onChange={(e) =>
                      setState((prev) => {
                        return { ...prev, volume: e.target.value };
                      })
                    }
                    value={state.volume}
                    step={1}
                    min={0}
                    max={5}
                  />
                  <Box
                    sx={{
                      bgcolor: 'divider',
                      borderRadius: 50,
                      px: 1,
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2">{state.volume}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box mb={1}>
                <Typography variant="body1" gutterBottom>
                  Sleep timeout
                </Typography>
                <Select
                  onChange={(e) =>
                    setState((prev) => {
                      return { ...prev, sleep: e.target.value };
                    })
                  }
                  value={state.sleep}
                  fullWidth
                  sx={{ borderRadius: 50, px: 1 }}
                  IconComponent={ArrowDropDownIcon}
                >
                  <MenuItem value={3}>3 sec</MenuItem>
                  <MenuItem value={5}>5 sec</MenuItem>
                  <MenuItem value={10}>10 sec</MenuItem>
                </Select>
              </Box> */}
            </Box>
          </Box>
        )}
        {tab === TABS[1] && (
          <Box display="flex" flexDirection="column" height="360px" gap={6}>
            <Stack direction="row" alignItems="center" gap={2} width="100%">
              <Switch
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, canCompanion: !prev.canCompanion };
                  })
                }
                checked={state.canCompanion}
              />
              <Typography variant="body1" fontWeight={500}>
                CAN Companion
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap={2} width="100%">
              <Switch
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, firmware: !prev.firmware };
                  })
                }
                checked={state.firmware}
              />
              <Typography variant="body1" fontWeight={500}>
                Allow beta Firmware updates
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="flex-start" gap={2} width="100%">
              <Switch
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, fallDetection: !prev.fallDetection };
                  })
                }
                checked={state.fallDetection}
              />
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Fall Detection Configuration (Beta)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  When CAN Go detects a fall, it will call your assigned emergency contact if no action is taken. To
                  stop the call from initiating, simply press any of the control buttons during the beeping response
                  window.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="flex-start" gap={2} width="100%">
              <Switch
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, whitelist: !prev.whitelist };
                  })
                }
                checked={state.whitelist}
              />
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Whitelist (Beta)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  To eliminate unwanted calls the cane will only allow your caregivers list. You can disable this
                  feature here
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
        {tab === TABS[2] && (
          <Box className="device-caregiver-list">
            {!isEmpty(caregiversData) &&
              Object.values(caregiversData).map((item, idx) => (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                  bgcolor="rgba(245, 245, 247, 1)"
                  borderRadius="16px"
                  height="72px"
                  my={2}
                  key={idx}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      src={item?.userInfo?.imageUrl || 'image.png'}
                      alt={item?.userInfo?.firstName}
                      sx={{ width: 48, height: 48 }}
                    />
                    <Box>
                      <Typography variant="body1" fontWeight={500} color="rgba(29, 29, 31, 1)">
                        {item?.userInfo?.firstName} {item?.userInfo?.lastName || ''}
                      </Typography>
                      <Typography variant="body2" color="rgba(89, 89, 95, 1)">
                        {item?.username}
                      </Typography>
                    </Box>
                  </Box>

                  {user?.emergencyContact !== item?.username ? (
                    <Chip
                      label="Set as Emergency Contact"
                      sx={{
                        bgcolor: '#fff',
                        color: 'black',
                        height: 24,
                        borderRadius: '50px',
                        pl: 1,
                        pr: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleSetEmergency(user?._id, item?._id)}
                      disabled={isEmpty(
                        caregiversData[item?._id]?.devices?.find((item) => item?.deviceId === device?._id),
                      )}
                    />
                  ) : (
                    <Chip
                      icon={<WarningIcon style={{ color: 'red' }} />}
                      label="Emergency contact"
                      sx={{
                        bgcolor: '#fff',
                        color: 'black',
                        height: 24,
                        borderRadius: '50px',
                        pl: 1,
                        pr: 3,
                      }}
                    />
                  )}

                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      onChange={() => handleSwitchChange(item?._id, device?._id)}
                      checked={
                        !isEmpty(caregiversData[item?._id]?.devices?.find((item) => item?.deviceId === device?._id))
                      }
                      disabled={user?.emergencyContact === item?.username}
                    />
                  </Box>
                </Box>
              ))}
          </Box>
        )}

        <Typography variant="subtitle1" style={{ color: 'red', marginTop: '30px' }} textAlign="center">
          {state.errorMessage}
        </Typography>

        <Divider />

        <Box display="flex" justifyContent="space-between" pt={2}>
          <Button onClick={onClose} text="Cancel" bg="light" />
          <Button
            onClick={handleSubmit}
            loading={
              tab === TABS[0]
                ? updateDeviceInfoHandler.isLoading
                : tab === TABS[1]
                  ? updateDeviceConfigHandler.isLoading
                  : updateDeviceCaregivers.isLoading
            }
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default DeviceConfigurationsModal;
